import { useMemo, useState, useCallback } from "react";
import type { Answers } from "@/schema/quizSchema";
import { deriveProfile } from "../profile";
import { OFFER } from "../offer";
import { ResultPage } from "./ResultPage";
import { ProjectionPage } from "./ProjectionPage";
import { ProductPage } from "./ProductPage";
import { ApplyingPage } from "./ApplyingPage";

type Page = "result" | "projection" | "product" | "applying";

/**
 * The post-result funnel. The engine exposes a single renderResult hook, so all
 * surfaces live here behind a local page router (AURAL's pattern).
 *
 * THE FIX (product) is the exit point: its "CLAIM MY DISCOUNT" CTA runs the
 * discount-applying loader, then hands off to the live Manly body wash offer
 * page (OFFER.pdpHref). There is no in-quiz offer screen — price and discount
 * live on Manly, which is the only place they stay current.
 */
export function ManlyFunnel({ answers, trackCta }: { answers: Answers; trackCta?: () => void }) {
  const profile = useMemo(() => deriveProfile(answers), [answers]);
  const [page, setPage] = useState<Page>("result");
  const goal = typeof answers.goal === "string" ? answers.goal : "fresh";

  const toDeal = useCallback(() => {
    if (typeof window === "undefined") return;
    const utm =
      "utm_source=quiz&utm_medium=quiz&utm_campaign=teen-bo-profile" +
      `&utm_content=${encodeURIComponent(profile.verdict.toLowerCase())}`;
    window.location.href = `${OFFER.pdpHref}?${utm}`;
  }, [profile.verdict]);

  return (
    <div className="qk-manly">
      {/* Styles are injected once at the quiz level (QuizDefinition.styles), so
          they cover the discount loader too, not just this funnel. */}
      {page === "result" && (
        <ResultPage profile={profile} goal={goal} onNext={() => setPage("projection")} />
      )}
      {page === "projection" && (
        <ProjectionPage profile={profile} onNext={() => setPage("product")} />
      )}
      {page === "product" && (
        <ProductPage
          profile={profile}
          onNext={() => {
            // The claim-discount tap IS the CTA moment; the applying loader
            // gives the keepalive event POST ample time before the redirect.
            trackCta?.();
            setPage("applying");
          }}
        />
      )}
      {page === "applying" && <ApplyingPage onDone={toDeal} />}
    </div>
  );
}

export default ManlyFunnel;
